import { useEffect, useRef, useState } from "react";
import { CalendarDays } from "lucide-react";

function pad(n) {
  return String(n).padStart(2, "0");
}

function todayIso() {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function isoToDigits(iso) {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return "";
  const [y, m, d] = iso.split("-");
  return `${d}${m}${y}`;
}

function digitsToDisplay(digits) {
  const d = String(digits || "").replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}-${d.slice(2)}`;
  return `${d.slice(0, 2)}-${d.slice(2, 4)}-${d.slice(4)}`;
}

function digitsToIso(digits) {
  const d = String(digits || "").replace(/\D/g, "");
  if (d.length !== 8) return "";
  const day = Number(d.slice(0, 2));
  const month = Number(d.slice(2, 4));
  const year = Number(d.slice(4));
  const dt = new Date(year, month - 1, day);
  if (dt.getFullYear() !== year || dt.getMonth() !== month - 1 || dt.getDate() !== day) return "";
  return `${year}-${pad(month)}-${pad(day)}`;
}

/** After DD, month digits must be 01–12. 2–9 auto-become 02–09. */
export function constrainDobDigits(prevDigits, nextRaw) {
  const prev = String(prevDigits || "").replace(/\D/g, "").slice(0, 8);
  let next = String(nextRaw || "").replace(/\D/g, "").slice(0, 8);
  if (next.length < prev.length) return next;

  if (next.length >= 1) {
    const d1 = next[0];
    if (d1 > "3") return prev;
  }
  if (next.length >= 2) {
    const day = Number(next.slice(0, 2));
    if (day < 1 || day > 31) return prev.slice(0, 1);
  }

  if (prev.length === 2 && next.length >= 3) {
    const third = next[2];
    if (third >= "2" && third <= "9") {
      next = `${next.slice(0, 2)}0${third}${next.slice(3)}`;
    } else if (third !== "0" && third !== "1") {
      return prev;
    }
  }

  if (next.length >= 3) {
    const m1 = next[2];
    if (m1 !== "0" && m1 !== "1") return next.slice(0, 2);
  }
  if (next.length >= 4) {
    const month = Number(next.slice(2, 4));
    if (month < 1 || month > 12) return next.slice(0, 3);
  }
  return next.slice(0, 8);
}

export default function DmyDateInput({
  value = "",
  onChange,
  max,
  min,
  noFuture,
  required,
  className = "",
  "data-testid": testId,
  disabled,
}) {
  const [text, setText] = useState(() => digitsToDisplay(isoToDigits(value)));
  const pickerRef = useRef(null);
  const cap = noFuture ? (max && max < todayIso() ? max : todayIso()) : max;

  useEffect(() => {
    setText(digitsToDisplay(isoToDigits(value)));
  }, [value]);

  const applyIso = (iso) => {
    if (!iso) {
      setText("");
      onChange?.("");
      return;
    }
    if (cap && iso > cap) return;
    if (min && iso < min) return;
    setText(digitsToDisplay(isoToDigits(iso)));
    onChange?.(iso);
  };

  return (
    <div className="relative w-full">
      <input
        type="text"
        inputMode="numeric"
        autoComplete="bday"
        placeholder="DD-MM-YYYY"
        required={required}
        disabled={disabled}
        maxLength={10}
        value={text}
        data-testid={testId}
        className={`${className} pr-11`}
        onChange={(e) => {
          const nextDigits = constrainDobDigits(text, e.target.value);
          const next = digitsToDisplay(nextDigits);
          const iso = digitsToIso(next);
          if (iso) {
            if (cap && iso > cap) return;
            if (min && iso < min) return;
          }
          setText(next);
          if (iso) {
            onChange?.(iso);
          } else if (!next.replace(/\D/g, "")) {
            onChange?.("");
          }
        }}
      />
      <span className="absolute right-1.5 top-1/2 -translate-y-1/2 w-9 h-9">
        <CalendarDays className="absolute inset-0 m-auto w-4 h-4 text-[#673c96] pointer-events-none" />
        <input
          ref={pickerRef}
          type="date"
          tabIndex={-1}
          disabled={disabled}
          min={min || undefined}
          max={cap || undefined}
          value={value && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : ""}
          onChange={(e) => applyIso(e.target.value)}
          onClick={(e) => {
            const el = e.currentTarget;
            if (disabled) return;
            if (typeof el.showPicker === "function") {
              try {
                el.showPicker();
              } catch {
                /* native click still opens the picker */
              }
            }
          }}
          className="absolute inset-0 w-full h-full cursor-pointer opacity-0 disabled:cursor-not-allowed"
          aria-label="Open calendar"
          data-testid={testId ? `${testId}-calendar` : "date-calendar"}
        />
      </span>
    </div>
  );
}
